"use client";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { asistenciaProfesorService } from "../../../services/profesor/asistencia.service";
import { ProfesorLayout } from "../components/ProfesorLayout";
import { AsistenciaDia, AsistenciaLog } from "./dto/asistencia.dto";

const AsistenciaPage = () => {
  const [rows, setRows] = useState<AsistenciaLog[]>([]);
  const [filteredRows, setFilteredRows] = useState<AsistenciaLog[]>([]);
  const [filtroAsistencia, setFiltroAsistencia] = useState<string>("Todos");
  const [filtroFecha, setFiltroFecha] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  const [modalActividades, setModalActividades] = useState<string[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalFecha, setModalFecha] = useState<Date | null>(null);
  const [modalAlumno, setModalAlumno] = useState<string | null>(null);

  const [modalEditarVisible, setModalEditarVisible] = useState(false);
  const [editarRow, setEditarRow] = useState<AsistenciaLog | null>(null);

  const actividadesDisponibles = [
    { label: "Modo Historia", value: "modo_historia" },
    { label: "Simulación 10 preguntas", value: "respuesta_paes" },
    { label: "Ensayo PAES", value: "simulacion_paes" },
  ];

  const [actividadesSeleccionadas, setActividadesSeleccionadas] = useState<
    string[]
  >([]);
  const [estadoAsistencia, setEstadoAsistencia] = useState<
    "Presente" | "Ausente"
  >("Ausente");

  const toast = useRef<Toast>(null);

  const convertirAsistenciaDiaALog = (
    asistencia: AsistenciaDia,
    index: number
  ): AsistenciaLog => {
    const actividadesTexto =
      asistencia.actividades.length > 0
        ? asistencia.actividades
            .map((a) => {
              switch (a.tipo) {
                case "modo_historia":
                  return "Modo Historia";
                case "respuesta_paes":
                  return "Simulación 10 preguntas";
                case "simulacion_paes":
                  return "Ensayo PAES";
                default:
                  return a.descripcion;
              }
            })
            .join(", ")
        : "Sin actividad";

    const estado = asistencia.presente ? "Presente" : "Ausente";

    return {
      id: index,

      usuarioId: (asistencia as any).usuarioId ?? 0,
      alumno: asistencia.nombreCompleto,
      anioEscolar: asistencia.anioEscolar || "",
      cursoLetra: asistencia.curso || "",
      fecha: asistencia.fecha,
      accionDetectada: actividadesTexto,
      asistencia: estado,
      actividades: asistencia.actividades.map((a) => a.tipo),
    };
  };

  const obtenerAlumnosDisponibles = (asistencia: AsistenciaDia[]) => {
    const map = new Map();
    asistencia.forEach((item) => {
      if (!map.has(item.nombreCompleto)) {
        map.set(item.nombreCompleto, {
          label: item.nombreCompleto,
          value: item.nombreCompleto,
          anioEscolar: item.anioEscolar ?? "",
          cursoLetra: item.curso ?? "",
        });
      }
    });
    return Array.from(map.values());
  };

  const cargarAsistencia = async () => {
    try {
      setLoading(true);

      const response = await asistenciaProfesorService.getAsistencia();

      const logs = response.asistencia.map(convertirAsistenciaDiaALog);

      setRows(logs);
      setFilteredRows(logs);
      setAlumnosDisponibles(obtenerAlumnosDisponibles(response.asistencia));

      localStorage.setItem("asistenciaData", JSON.stringify(logs));
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo cargar la asistencia.",
      });
    } finally {
      setLoading(false);
    }
  };

  const [alumnosDisponibles, setAlumnosDisponibles] = useState<
    { label: string; value: string; anioEscolar: string; cursoLetra: string }[]
  >([]);

  useEffect(() => {
    cargarAsistencia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let temp = [...rows];

    if (filtroAsistencia !== "Todos") {
      temp = temp.filter((r) => r.asistencia === filtroAsistencia);
    }

    if (filtroFecha) {
      const f = filtroFecha.toISOString().split("T")[0];
      temp = temp.filter((r) => r.fecha === f);
    }

    setFilteredRows(temp);
  }, [filtroAsistencia, filtroFecha, rows]);

  const asistenciaTemplate = (rowData: AsistenciaLog) => (
    <Tag
      value={rowData.asistencia}
      severity={rowData.asistencia === "Presente" ? "success" : "danger"}
      rounded
    />
  );

  const botonesTemplate = (rowData: AsistenciaLog) => (
    <Button
      icon="pi pi-pencil"
      rounded
      text
      onClick={() => abrirModalEditar(rowData)}
    />
  );

  const abrirModalEditar = (row: AsistenciaLog) => {
    setEditarRow(row);

    const actuales = row.actividades ?? [];
    setActividadesSeleccionadas(actuales);
    setEstadoAsistencia(actuales.length > 0 ? "Presente" : "Ausente");
    setModalEditarVisible(true);
  };

  const guardarEdicion = async () => {
    if (!editarRow) return;

    const payload = {
      usuarioId: editarRow.usuarioId,
      fecha: editarRow.fecha,
      actividades:
        estadoAsistencia === "Presente" ? actividadesSeleccionadas : [],
    };

    if (estadoAsistencia === "Presente" && payload.actividades.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Falta información",
        detail: "Debe seleccionar al menos una actividad.",
      });
      return;
    }

    console.log("PAYLOAD ENVIADO →", payload);

    try {
      const resp = await asistenciaProfesorService.actualizarAsistencia(
        payload
      );

      if (resp.status !== 200 && resp.status !== 204) {
        toast.current?.show({
          severity: "warn",
          summary: "Advertencia",
          detail: resp?.message || "No se pudo actualizar la asistencia.",
        });
        return;
      }

      toast.current?.show({
        severity: "success",
        summary: "Actualizado",
        detail: "Asistencia editada correctamente.",
      });

      setModalEditarVisible(false);
      cargarAsistencia();
    } catch (e: any) {
      console.log("ERROR BACKEND →", e?.response?.data || e);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          e?.response?.data?.message ||
          e?.message ||
          "Error desconocido al guardar",
      });
    }
  };

  const guardarManual = async () => {
    if (!modalAlumno || !modalFecha) return;

    const fechaStr = modalFecha.toISOString().split("T")[0];
    const alumnoData = alumnosDisponibles.find((a) => a.value === modalAlumno);

    const existe = rows.some(
      (r) => r.alumno === modalAlumno && r.fecha === fechaStr
    );

    if (existe) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          "Ya existe un registro de asistencia en esa fecha para el estudiante.",
      });
      return;
    }

    const usuarioIdReal =
      rows.find((r) => r.alumno === modalAlumno)?.usuarioId ?? 0;

    const presente = modalActividades.length > 0;

    const payload = {
      usuarioId: usuarioIdReal,
      fecha: fechaStr,
      actividades: modalActividades,
    };

    console.log("📤 PAYLOAD MANUAL →", payload);

    try {
      const resp = await asistenciaProfesorService.actualizarAsistencia(
        payload
      );

      if (resp.status !== 200 && resp.status !== 201 && resp.status !== 204) {
        toast.current?.show({
          severity: "warn",
          summary: "Advertencia",
          detail: resp?.message || "No se pudo registrar la asistencia.",
        });
        return;
      }

      const nuevo: AsistenciaLog = {
        id: rows.length + 1,
        usuarioId: usuarioIdReal,
        alumno: modalAlumno,
        anioEscolar: alumnoData?.anioEscolar ?? "",
        cursoLetra: alumnoData?.cursoLetra ?? "",
        fecha: fechaStr,
        accionDetectada: presente
          ? modalActividades.join(", ")
          : "Sin actividad",
        asistencia: presente ? "Presente" : "Ausente",
        actividades: modalActividades,
      };

      const actualizados = [...rows, nuevo];
      setRows(actualizados);

      localStorage.setItem("asistenciaData", JSON.stringify(actualizados));

      setModalActividades([]);
      setEstadoAsistencia("Ausente");
      setModalAlumno(null);
      setModalFecha(null);
      setModalVisible(false);

      toast.current?.show({
        severity: "success",
        summary: "Guardado",
        detail: "Asistencia registrada correctamente.",
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo guardar en el servidor.",
      });
    }
  };

  return (
    <ProfesorLayout>
      <Toast ref={toast} />

      {/* Header */}
      <div className="mb-10">
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl shadow-lg p-8 text-white">
          <h1 className="text-4xl font-bold mb-2">Control de Asistencia</h1>
          <p className="text-purple-100 text-lg">
            Revisión de asistencia basada en actividad.
          </p>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtros</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-gray-700 font-medium">Asistencia</label>
            <Dropdown
              value={filtroAsistencia}
              options={["Todos", "Presente", "Ausente"]}
              onChange={(e) => setFiltroAsistencia(e.value)}
              className="w-full mt-2"
            />
          </div>

          <div>
            <label className="text-gray-700 font-medium">Fecha</label>
            <Calendar
              value={filtroFecha}
              onChange={(e) => setFiltroFecha(e.value ?? null)}
              dateFormat="yy-mm-dd"
              className="w-full mt-2"
              showIcon
            />
          </div>

          <div className="flex items-end">
            <Button
              label="Limpiar filtros"
              outlined
              className="w-full"
              onClick={() => {
                setFiltroAsistencia("Todos");
                setFiltroFecha(null);
              }}
            />
          </div>
        </div>
      </div>

      <Button
        label="Ingresar asistencia manual"
        icon="pi pi-plus"
        onClick={() => setModalVisible(true)}
        className="bg-blue-600 hover:bg-blue-800 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition-all"
      />

      {/* Tabla */}
      <div className="bg-white shadow-md rounded-lg p-6 mt-6">
        <DataTable
          value={filteredRows}
          paginator
          rows={8}
          rowsPerPageOptions={[5, 10, 20]}
          className="text-sm"
          stripedRows
          loading={loading}
          emptyMessage="No hay registros de asistencia"
        >
          <Column field="fecha" header="Fecha" sortable />
          <Column field="alumno" header="Alumno" sortable />
          <Column field="anioEscolar" header="Año Escolar" sortable />
          <Column field="cursoLetra" header="Curso" sortable />
          <Column field="accionDetectada" header="Log Detectado" />
          <Column header="Asistencia" body={asistenciaTemplate} sortable />
          <Column header="Acciones" body={botonesTemplate} />
        </DataTable>
      </div>

      <Dialog
        header="Registrar asistencia manual"
        visible={modalVisible}
        style={{ width: "420px" }}
        modal
        onHide={() => setModalVisible(false)}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="font-medium text-gray-700">Fecha</label>
            <Calendar
              value={modalFecha}
              onChange={(e) => setModalFecha(e.value ?? null)}
              className="w-full mt-2"
              showIcon
            />
          </div>

          <div>
            <label className="font-medium text-gray-700">Estudiante</label>
            <Dropdown
              value={modalAlumno}
              options={alumnosDisponibles}
              placeholder="Seleccionar estudiante"
              onChange={(e) => setModalAlumno(e.value)}
              className="w-full mt-2"
              filter
              filterPlaceholder="Buscar estudiante"
              showClear
            />
          </div>

          <div>
            <label className="font-medium text-gray-700">
              Actividades realizadas
            </label>

            <div className="flex flex-col gap-2 mt-2">
              {actividadesDisponibles.map((a) => (
                <div key={a.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={modalActividades.includes(a.value)}
                    onChange={() => {
                      setModalActividades((prev) => {
                        const nuevas = prev.includes(a.value)
                          ? prev.filter((x) => x !== a.value)
                          : [...prev, a.value];
                        setEstadoAsistencia(
                          nuevas.length > 0 ? "Presente" : "Ausente"
                        );

                        return nuevas;
                      });
                    }}
                  />
                  <span>{a.label}</span>
                </div>
              ))}
            </div>

            <small className="text-gray-500">
              Si seleccionas alguna actividad, la asistencia se marcará como{" "}
              <b>Presente</b>.
            </small>
          </div>

          <Button
            label="Guardar asistencia"
            onClick={guardarManual}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white font-bold py-3 rounded-xl shadow-lg border-0 transition-all duration-300"
          />
        </div>
      </Dialog>

      <Dialog
        header="Editar asistencia"
        visible={modalEditarVisible}
        style={{ width: "480px" }}
        modal
        onHide={() => setModalEditarVisible(false)}
      >
        {editarRow && (
          <div className="flex flex-col gap-4">
            <div>
              <label className="font-medium text-gray-700">
                Actividades realizadas
              </label>

              <div className="flex flex-col gap-2 mt-2">
                {actividadesDisponibles.map((a) => (
                  <div key={a.value} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={actividadesSeleccionadas.includes(a.value)}
                      onChange={() => {
                        setActividadesSeleccionadas((prev) => {
                          const nuevas = prev.includes(a.value)
                            ? prev.filter((x) => x !== a.value)
                            : [...prev, a.value];

                          setEstadoAsistencia(
                            nuevas.length > 0 ? "Presente" : "Ausente"
                          );

                          return nuevas;
                        });
                      }}
                    />

                    <span>{a.label}</span>
                  </div>
                ))}
              </div>

              <small className="text-gray-500">
                Si no selecciona actividades, se marcará como <b>Ausente</b>.
              </small>
            </div>

            <Button
              label="Guardar cambios"
              className="w-full bg-blue-600 text-white"
              onClick={guardarEdicion}
            />
          </div>
        )}
      </Dialog>
    </ProfesorLayout>
  );
};

export default AsistenciaPage;
